import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  CursorSpotlight,
  Counter,
  RevealWords,
  useMagnetic,
} from "@/components/effects";
import IPhoneMockup from "@/components/IPhoneMockup";

export default function Hero() {
  const magCta = useMagnetic(0.3);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-24 md:pt-40 md:pb-32 border-b border-white/10 grid-bg overflow-hidden"
    >
      <CursorSpotlight />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-yellow-400" />
            <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
              Conversation-Native Software
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light leading-[0.95] text-white"
          >
            <RevealWords>Software that</RevealWords>
            <br />
            <RevealWords
              className="italic font-extralight text-zinc-400"
              baseDelay={0.18}
            >
              disappears
            </RevealWords>
            <br />
            <RevealWords baseDelay={0.32}>into the apps</RevealWords>
            <br />
            <RevealWords baseDelay={0.52}>you already use.</RevealWords>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed font-light text-zinc-400"
          >
            Invisible System Design builds resilient, contemplative tools that live
            entirely within iMessage, WhatsApp, Telegram, and Slack. No new app to
            download. No new habit to form. Just messages — that do work.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              ref={magCta}
              href="sms:+13104295413?&body=Hey%20Robbe!%20I'm%20so%20intrigued%2C%20what%20do%20you%20need%20for%20me%20to%20start%3F"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-yellow-400 text-black px-7 py-4 text-sm uppercase tracking-[0.2em] font-medium hover:bg-yellow-500"
            >
              Start Your Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
            </a>
            <Link
              to="/approach"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-4 text-sm uppercase tracking-[0.2em] font-medium hover:bg-white/5 hover:border-white/40 transition-colors"
            >
              See the Approach
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            <div className="border-l border-white/10 pl-4">
              <div className="font-heading text-2xl font-light text-white">
                <Counter value={4} />
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">Platforms</div>
            </div>
            <div className="border-l border-white/10 pl-4">
              <div className="font-heading text-2xl font-light text-white">
                <Counter value={48} suffix="h" />
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">First build</div>
            </div>
            <div className="border-l border-white/10 pl-4">
              <div className="font-heading text-2xl font-light text-white">
                <Counter value={99.9} decimals={1} suffix="%" />
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">Uptime</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 relative fade-up">
          <div className="relative w-full max-w-md mx-auto flex items-center justify-center">
            <IPhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}
